import ClickableLink from 'components/Common/Clickable/ClickableLink'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import PaletteSheet from 'LuxKit/PaletteSheet'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isString } from 'lib/string/stringUtils'
import { stripMarkdown } from 'lib/string/stripMarkdown'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import clsx from 'clsx'
import getResizableImage from '../getResizableImage'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

export const CAROUSEL_CARD_WIDTH = 320

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: ${rem(16)};
  height: 100%;
  position: relative;
  min-height: ${rem(300)};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(400)};
  }

  &.variant-opaque {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;

  body.${REBRAND_PALETTE_CLASS} &.rounded {
    border-radius: var(--border-radius-m);
    overflow: hidden;
  }
`

const OfferImageCarousel = styled(ImageCarousel)`
  height: 100%;
`

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(8)} ${rem(12)};
  pointer-events: none;
  justify-content: space-between;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

const IMAGE_ASPECT_RATIO = '16:9'
const IMAGE_WIDTH = 320
const IMAGE_PARAMS: ImageParams = {
  mobileAspectRatio: IMAGE_ASPECT_RATIO,
  tabletAspectRatio: IMAGE_ASPECT_RATIO,
  mobileWidth: `${IMAGE_WIDTH}px`,
  tabletWidth: `${IMAGE_WIDTH}px`,
  desktopWidth: `${IMAGE_WIDTH}px`,
  quality: 'best',
  fit: 'center',
  dpr: 2,
}

function getPlainSummaryDescription(description: string) {
  return stripMarkdown(description.replace(/!\[[^\]]*\]\([^)]*\)/g, ''))
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const DetailsContainer = styled(Group)`
  overflow: hidden;

  &.variant-opaque {
    padding-block: 0 ${rem(16)};
    padding-inline: ${rem(16)};
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  bookmarkButton?: React.ReactElement
  description?: string | React.ReactElement
  descriptionLines?: number
  image: App.Image
  location: string | React.ReactElement
  title: string
  priceDetails?: React.ReactElement
  to: string
  onClick?: () => void
  rating?: App.OfferRating
  urgencyLabels?: React.ReactElement
  partnershipLabel?: React.ReactElement
  loyaltyPointsElement?: React.ReactElement
  loyaltyPackageUpgradeElement?: React.ReactElement
  imageCornerLabel?: React.ReactElement
  images?: Array<App.Image>
  taglineInclusions?: React.ReactElement
  /**
   * @default default
   */
  variant?: 'default' | 'opaque'
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean
  isHoverPreviewActive?: boolean
}

function CarouselCardSmall({
  bookmarkButton,
  description,
  descriptionLines,
  image: baseImage,
  images,
  location,
  title,
  priceDetails,
  to,
  onClick,
  rating,
  urgencyLabels,
  partnershipLabel,
  className,
  loyaltyPointsElement,
  loyaltyPackageUpgradeElement,
  imageCornerLabel,
  taglineInclusions,
  variant = 'default',
  eagerLoadImage,
  isHoverPreviewActive,
  ...rest
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const handleImageLoaded = useCallback((index: number) => {
    if (index === 0) {
      dispatchOfferListEvent({ type: OfferListEvents.offerReady })
    }
  }, [dispatchOfferListEvent])

  const carouselImages = useMemo<Array<App.Image>>(() => [
    baseImage ?? images?.[0],
    ...(images?.slice(1) ?? []),
  ].map(getResizableImage).filter((image): image is App.Image => !!image), [baseImage, images])
  const plainDescription = useMemo(() => {
    return isString(description) ? getPlainSummaryDescription(description) : undefined
  }, [description])

  return <CarouselCardContainer
    {...rest}
    onClick={onClick}
    target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
    to={to}
    className={clsx(className, `variant-${variant}`)}
  >
    <ImageContainer
      className={clsx({ rounded: variant === 'default' })}
      paletteType="default"
    >
      <OfferImageCarousel
        images={carouselImages}
        imageParams={IMAGE_PARAMS}
        eagerLoadFirstImage={eagerLoadImage}
        onImageLoad={handleImageLoaded}
        arrowBehaviour="hidden"
        showImageCounter={false}
        hoverPreviewBehaviour="crossfade"
        isHoverPreviewActive={isHoverPreviewActive}
      />
      <ImageOverlay>
        <LabelGroup className="left">
          {imageCornerLabel}
          {partnershipLabel}
        </LabelGroup>
        <TripGuard>
          <div className="right">
            {bookmarkButton}
          </div>
        </TripGuard>
      </ImageOverlay>
    </ImageContainer>
    <DetailsContainer
      direction="vertical"
      verticalAlign="space-between"
      gap={16}
      className={`variant-${variant}`}
    >
      <Group direction="vertical" gap={8}>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
          {isString(location) ? <Caption colour="normal-contrast" variant="large" lineClamp={1}>{location}</Caption> : location}
          {rating && <OfferRating
            variant="xs"
            rating={rating}
            onlyNumber
            inlineLabel
            hideLink
          />}
        </Group>
        <Heading lineClamp={3} variant="heading6">{title}</Heading>
        {taglineInclusions}
        {plainDescription && <BodyText colour="light-foreground-alt" lineClamp={descriptionLines || 2} variant="medium" wrap="word-wrap">{plainDescription}</BodyText>}
        {description && !isString(description) && description}
        {loyaltyPackageUpgradeElement}
        {loyaltyPointsElement}
        {urgencyLabels}
      </Group>
      <div>
        {priceDetails}
      </div>
    </DetailsContainer>
  </CarouselCardContainer>
}

export default CarouselCardSmall
